'use client'

import React, { useEffect, useState } from 'react';
import { Link, IconButton, Drawer, List, ListItem } from "@mui/material";
import Badge from '@mui/material/Badge';
import LogoComponent from "../logo";
import styles from "./header.module.scss";
import { gtmClickTracking, navigateWithHistory } from "@/utils";
import { getUserCartInfo } from "@/utils/useUserInfo";
import { useRouter, usePathname } from 'next/navigation';
import { signOut } from '@nestingale/auth';
import { UserProfileBadge } from '@nestingale/auth/components';
import { useAuth } from '@nestingale/auth/hooks';
import Image from 'next/image';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import MenuIcon from '@mui/icons-material/Menu';
import Divider from '@mui/material/Divider';
import shoppingCartIcon from '@/assets/images/shopping_cart.svg';
import signoutIcon from '@/assets/images/signout.svg';
import searchOrangeIcon from '@/assets/images/UnionOrange.png'
import searchBlackIcon from '@/assets/images/Union.png'
import dynamic from 'next/dynamic';
import HeaderMenu from './menu/header_menu';
import { transformTextToHtml } from "@/app/components/cms_renderer/shared";
import { getUserTypeFromUrlLs } from "@/utils/useUserInfo";
import { useLayout } from '@/app/providers/LayoutProvider';

const LoginLayer = dynamic(() => import('@/app/components/login'), {
    ssr: false,
});

export interface HeaderComponentProps {
    brand?: any;
    type?: 'full' | 'short';
    globalData?: any;
    headerMenuJson?: any;
    headerTopCmsData?: any;
    headerBottomCmsData?: any;
}

const HeaderComponent: React.FC<HeaderComponentProps> = ({
    brand,
    type = 'full',
    globalData,
    headerMenuJson = {},
    headerTopCmsData,
    headerBottomCmsData
}) => {
    const cmsBanner = headerTopCmsData?.items?.[0]?.content;
    const brandName = brand?.vendorName || brand?.name || 'nestingale';
    const router = useRouter();
    const pathname = usePathname();
    
    const lsv = globalData?.lsv || { showCart: true };
    const userInfo = globalData?.userInfo;
    
    const [cartCount, setCartCount] = useState(0);
    const [showProgress, setShowProgress] = useState(true);
    const [createRoomPlan, setCreateRoomPlan] = useState<string | null>(null);
    // GTR-format: header JSON has a `nav` array instead of a nested menu object.
    // Transform it into the { [key]: MenuItem } shape that HeaderMenu expects.
    const normaliseMenuJson = (raw: any) => {
        if (!raw || typeof raw !== 'object') return raw;
        if (Array.isArray(raw.nav)) {
            return raw.nav.reduce((acc: any, item: any) => {
                const key = item.key || item.name;
                acc[key] = item;
                return acc;
            }, {});
        }
        return raw;
    };
    const [menuJson, setMenuJson] = useState(() => normaliseMenuJson(headerMenuJson));
    const { isAuthenticated: isUserAuthenticated, isAuthChecked, userEmail } = useAuth();
    const [loginEl, setLoginEl] = useState<null | HTMLElement>(null);
    // Use context searchBarOpen so BaseLayoutTemplate can render SearchBar when open
    const layoutContext = useLayout();
    const searchBarOpen = layoutContext.searchBarOpen;
    const setSearchBarOpen = layoutContext.setSearchBarOpen;

    // Derive per-feature visibility flags from optional config block in headerMenuJson.
    // Defaults to true (show) when no config is present — all non-realtor pages are unaffected.
    const headerVisibilityConfig = headerMenuJson?.config;
    const showSearch = headerVisibilityConfig?.search !== false;
    const showLogin  = headerVisibilityConfig?.login  !== false;
    const showSignup = headerVisibilityConfig?.signup !== false;
    const showCart   = headerVisibilityConfig?.cart   !== false;
    //console.log("headerVisibilityConfig", headerVisibilityConfig, { showSearch, showLogin, showSignup, showCart });
    const { userType, isInteriorDesigner, isProductSupplier } = getUserTypeFromUrlLs();

    // Read createRoomPlan from URL on client side (avoids useSearchParams + Suspense requirement)
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        setCreateRoomPlan(params.get('createRoomPlan'));
    }, [pathname]);
    const currentPath = pathname;
    
    const isAssistantPage = isInteriorDesigner || isProductSupplier;
    const isCartPage = () => pathname?.includes('/cart');

    // Update menuJson when headerMenuJson prop changes
    useEffect(() => {
        if (headerMenuJson && typeof headerMenuJson === 'object' && Object.keys(headerMenuJson).length > 0) {
            setMenuJson(normaliseMenuJson(headerMenuJson));
        }
    }, [headerMenuJson]);

    // Navigation guard: redirect authenticated users from landing pages to app pages
    useEffect(() => {
        if (!isUserAuthenticated) return;
        const redirectOnValidSession: Record<string, string> = {
            '/interior-designer': '/ai/designer',
            '/product-supplier': '/ai/supplier',
            '/campaign': '/social'
        };
        const matchedPath = Object.keys(redirectOnValidSession).find(path =>
            currentPath.startsWith(path)
        );
        if (matchedPath && currentPath !== redirectOnValidSession[matchedPath]) {
            console.log("Redirecting to:", redirectOnValidSession[matchedPath]);
            window.location.href = redirectOnValidSession[matchedPath];
        }
    }, [isUserAuthenticated, currentPath]);

    useEffect(() => {
        try {
            if (userInfo && userInfo?.status) {
                if (userInfo?.status === "DONE") {
                    setShowProgress(false);
                    getUserCartInfo(userInfo)?.then((cartData: any) => {
                        setCartCount(cartData?.cartCount || 0);
                        userInfo.crtD = cartData;
                    })
                } else {
                    setShowProgress(true);
                }
            }
        } catch (e) {
            console.log("Error in HeaderComponent getUserCartInfo useEffect", e);
        }
    }, [userInfo]);

    const navigatorToLogin = () => {
        if ((window as any).checkingForUserSession || userEmail) {
            return;
        }
        window.location.href = `/showroom/${brandName}/login?next=${encodeURIComponent(window.location.href)}`
    }

    const signOutAiUser = async () => {
        try {
            localStorage.removeItem("ai-user-type");
            const isDedicatedDomain = !window.location.pathname.startsWith('/showroom/');
            const redirectTo = isDedicatedDomain
                ? '/'
                : userType === 'designer' ? `/showroom/${brandName}/interior-designer` : userType === 'supplier' ? `/showroom/${brandName}/product-supplier` : `/showroom/${brandName}/`;
            await signOut({
                sessionEndpoint: '/showroom/api/auth/session',
                onComplete: () => { window.location.href = redirectTo; },
            });
        } catch (error) {
            console.error("Error signing out:", error);
        }
    }

    const navigateToOrderHistory = () => {
        router.push('/orderhistory');
    }

    const navigateToCart = () => {
        router.push('/cart');
    }

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleSignOut = () => {
        signOut({
            sessionEndpoint: '/showroom/api/auth/session',
            onComplete: async () => {
                // App-specific cleanup
                if (typeof window !== 'undefined') {
                    try {
                        const { removeCustStyleProfileIdFromLS, removeCustIdFromLS, removeStpRoomTypeSel, CART_TOKEN, GCART_TOKEN } = await import('@nestingale/auth');
                        removeCustStyleProfileIdFromLS();
                        removeCustIdFromLS();
                        removeStpRoomTypeSel();
                        // @ts-ignore
                        window.setCookieWithoutSecure(CART_TOKEN, null, 10);
                        // @ts-ignore
                        window.setCookieWithoutSecure(GCART_TOKEN, null, 10);
                    } catch (e) { /* ignore */ }
                }
                // Call app signout API and redirect
                try {
                    const { default: axiosObj } = await import('@/store/axios');
                    const url = `${location.protocol}//${location.host}/showroom/api/signoutuser`;
                    await axiosObj.get(url);
                } catch (e) { /* ignore */ }
                const isDedicatedDomain = !window.location.pathname.startsWith('/showroom/');
                if (isDedicatedDomain) {
                    window.location.href = '/';
                } else {
                    const vendor = window.location.pathname.split('/')[2] || 'nestingale';
                    window.location.href = `/showroom/${vendor}/rooms`;
                }
            },
        });
    };

    const openLoginLayer = Boolean(loginEl);
    const handleLoginClick = (event: React.MouseEvent<HTMLElement>) => {
        setLoginEl(event.currentTarget);
    };
    const handleLoginClose = () => {
        setLoginEl(null);
    };

    // Auto-close login layer when auth state changes to authenticated
    useEffect(() => {
        if (isUserAuthenticated && loginEl) {
            handleLoginClose();
        }
    }, [isUserAuthenticated]);

    // Expose a global function so other components (e.g. partner signup modal) can open the sign-in layer
    useEffect(() => {
        (window as any).openSignIn = () => {
            const signInEl = document.getElementById('sign_in');
            if (signInEl) {
                setLoginEl(signInEl);
            }
        };
        return () => {
            delete (window as any).openSignIn;
        };
    }, []);
    const loginCallback = (callbackObj: any) => {
        console.log("loginCallback called", callbackObj);
        globalData?.loginCallback?.(callbackObj);
    };

    const [openDrawer, setOpenDrawer] = useState(false);
    const toggleDrawer = (open: boolean) => () => {
        setOpenDrawer(open)
    };

    function handleSearchIconClick() {
        setSearchBarOpen(!searchBarOpen)
        if (!searchBarOpen) {
            gtmClickTracking({
                event: 'search_bar_open'
            })
        }
        if (!searchBarOpen && typeof window !== "undefined") {
            window.scrollTo({
                top: 0,
                behavior: 'smooth'
            });
        }
    }

    // Determine logo alignment from CMS config (default: 'left' for non-vendor, depends on brand)
    const logoAlignment: 'left' | 'center' | 'right' = headerMenuJson?.logoAlignment || 'left';
    const ctaButtons: any[] = headerMenuJson?.cta || [];
    const hasCtaButtons = ctaButtons.length > 0;

    // Determine the headsection class based on logo alignment
    const headsectionClass = `${styles.headsection} ${logoAlignment === 'center' ? styles.headsection_logo_center : logoAlignment === 'right' ? styles.headsection_logo_right : styles.headsection_logo_left}`;

    return (
        <>
            <div className={styles.headerSectionWrapper}>
                {cmsBanner && <div className={styles.promo_banner}>
                    <span>{transformTextToHtml(cmsBanner)}</span>
                </div>}
                <div className={headsectionClass} style={headerMenuJson?.style ? { backgroundColor: headerMenuJson.style.background, height: headerMenuJson.style.height, borderBottom: headerMenuJson.style.borderBottom, position: headerMenuJson.style.position as any, top: headerMenuJson.style.top, zIndex: headerMenuJson.style.zIndex } : {}}>
                    {/* Hamburger menu - always rendered for SSR, CSS controls visibility (hidden on desktop, visible on mobile) */}
                    <IconButton
                        size="large"
                        edge="start"
                        color="inherit"
                        aria-label="menu"
                        className={styles.hamberger_btn}
                        onClick={toggleDrawer(true)}
                        sx={{ 
                            mr: 2,
                            display: 'none',
                            '@media screen and (max-width: 1024px)': {
                                display: 'flex'
                            }
                        }}
                    >
                        <MenuIcon />
                    </IconButton>

                    {/* --- LEFT-aligned logo: logo first, then nav --- */}
                    {logoAlignment === 'left' && (
                        <>
                            <div className={styles.logosection}>
                                <LogoComponent brand={brand} footer={false} isAssistantPage={isAssistantPage} logoConfig={headerMenuJson?.logo || menuJson?.logo} />
                            </div>
                            {type === 'full' &&
                                <div className={styles.middle_section}>
                                    <HeaderMenu menuJson={menuJson || {}} deviceType="desktop" />
                                </div>
                            }
                        </>
                    )}

                    {/* --- CENTER-aligned logo: nav left, logo center, actions right --- */}
                    {logoAlignment === 'center' && (
                        <>
                            {type === 'full' &&
                                <div className={styles.middle_section_left}>
                                    <HeaderMenu menuJson={menuJson || {}} deviceType="desktop" />
                                </div>
                            }
                            <div className={styles.logosection_center}>
                                <LogoComponent brand={brand} footer={false} isAssistantPage={isAssistantPage} logoConfig={headerMenuJson?.logo || menuJson?.logo} />
                            </div>
                        </>
                    )}

                    {/* --- RIGHT-aligned logo: nav first, then logo on right --- */}
                    {logoAlignment === 'right' && (
                        <>
                            {type === 'full' &&
                                <div className={styles.middle_section}>
                                    <HeaderMenu menuJson={menuJson || {}} deviceType="desktop" />
                                </div>
                            }
                            <div className={styles.logosection}>
                                <LogoComponent brand={brand} footer={false} isAssistantPage={isAssistantPage} logoConfig={headerMenuJson?.logo || menuJson?.logo} />
                            </div>
                        </>
                    )}

                    {
                        (lsv?.showCart && !createRoomPlan) &&
                        (<div className={styles.login__section}>
                            {/* Search icon - always visible, outside auth-check visibility wrapper */}
                            {!isAssistantPage && showSearch && (
                                <Image src={searchBarOpen ? searchOrangeIcon : searchBlackIcon} alt="search" width={18} height={18} onClick={handleSearchIconClick} style={{ cursor: 'pointer' }} />
                            )}
                            <div style={!isAuthChecked ? { visibility: 'hidden', display: 'flex', alignItems: 'center', columnGap: '10px' } : { display: 'flex', alignItems: 'center', columnGap: '10px' }}>
                            {
                                isUserAuthenticated ?
                                    <>
                                        {showLogin && (
                                            <UserProfileBadge
                                                isAuthenticated={true}
                                                onProfileClick={handleClick}
                                                onSignOut={handleSignOut}
                                            />
                                        )}
                                    </> :
                                    <>
                                        {/* CTA-driven sign-in button or fallback */}
                                        {(showLogin || showSignup) && (hasCtaButtons ? (
                                            <>
                                            {ctaButtons.filter(c => c.id === 'signin').map((cta) => (
                                                <button
                                                    key={cta.id}
                                                    id="sign_in"
                                                    className={styles.cta_button}
                                                    style={cta.style ? {
                                                        background: cta.style.background || 'transparent',
                                                        color: cta.style.color || '#000',
                                                        borderRadius: cta.style.borderRadius || '27.5px',
                                                        border: cta.style.border || '2px solid #000',
                                                        fontFamily: cta.style.fontFamily || 'Archivo',
                                                        fontWeight: cta.style.fontWeight || 600,
                                                        fontSize: cta.style.fontSize || '16px',
                                                        letterSpacing: cta.style.letterSpacing || '2%',
                                                        height: cta.style.height || '35px',
                                                        paddingLeft: cta.style.paddingLeft || '24px',
                                                        paddingRight: cta.style.paddingRight || '24px',
                                                    } : {}}
                                                    onClick={(e) => {
                                                        if (cta.trackInfo) gtmClickTracking(cta.trackInfo);
                                                        handleLoginClick(e);
                                                    }}
                                                >
                                                    {cta.text}
                                                </button>
                                            ))}
                                            {/* Mobile-only: profile icon shown instead of hidden CTA button */}
                                            <span className={styles.sign_in_mobile_only}>
                                                <UserProfileBadge
                                                    isAuthenticated={false}
                                                    onSignInClick={handleLoginClick}
                                                    signInLabel=""
                                                    style={{ border: 'none', padding: 0, height: 'auto' }}
                                                />
                                            </span>
                                            </>
                                        ) : (
                                            <UserProfileBadge
                                                isAuthenticated={false}
                                                onSignInClick={handleLoginClick}
                                                signInLabel="SIGN IN"
                                            />
                                        ))}
                                    </>
                            }
                            {/* Render non-signin CTA buttons (e.g., Open trade account) — only when logged out */}
                            {!isUserAuthenticated && hasCtaButtons && ctaButtons.filter(c => c.id !== 'signin').map((cta) => (
                                <a
                                    key={cta.id}
                                    href={cta.action ? '#' : (cta.link || '#')}
                                    className={styles.cta_button}
                                    style={cta.style ? {
                                        background: cta.style.background || 'transparent',
                                        color: cta.style.color || '#000',
                                        borderRadius: cta.style.borderRadius || '27.5px',
                                        border: cta.style.border || '2px solid #000',
                                        fontFamily: cta.style.fontFamily || 'Archivo',
                                        fontWeight: cta.style.fontWeight || 600,
                                        fontSize: cta.style.fontSize || '16px',
                                        letterSpacing: cta.style.letterSpacing || '2%',
                                        height: cta.style.height || '35px',
                                        paddingLeft: cta.style.paddingLeft || '24px',
                                        paddingRight: cta.style.paddingRight || '24px',
                                    } : {}}
                                    onClick={(e) => {
                                        if (cta.action) {
                                            e.preventDefault();
                                            window.openSignUpPopup?.(cta.action);
                                        }
                                        if (cta.trackInfo) gtmClickTracking(cta.trackInfo);
                                    }}
                                >
                                    {cta.text}
                                </a>
                            ))}
                            {/* Cart — only visible when logged in, always at the end of the row */}
                            {isUserAuthenticated && !isCartPage() && !isAssistantPage && showCart && (
                                <Link className={styles.cart_link} onClick={navigateToCart} sx={{ color: 'primary.main', cursor: 'pointer' }}>
                                    <Badge id="header_cart_icon" badgeContent={cartCount} color="primary" sx={{ cursor: 'pointer' }}>
                                        <Image src={shoppingCartIcon} width={19} height={19} alt="Cart" />
                                    </Badge>
                                </Link>
                            )}
                            {isUserAuthenticated && !isAssistantPage ?
                                <Menu
                                    anchorEl={anchorEl}
                                    id="account-menu"
                                    className={styles.menu_list}
                                    open={open}
                                    onClose={handleClose}
                                    onClick={handleClose}
                                    PaperProps={{
                                        elevation: 0,
                                        sx: {
                                            overflow: 'visible',
                                            borderRadius: '12px',
                                            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                                            mt: 1.5,
                                            '& .MuiAvatar-root': {
                                                width: 32,
                                                height: 32,
                                                ml: -0.5,
                                                mr: 1,
                                            },
                                            '&:before': {
                                                content: '""',
                                                display: 'block',
                                                position: 'absolute',
                                                top: 0,
                                                right: 20,
                                                width: 15,
                                                height: 15,
                                                bgcolor: 'background.paper',
                                                transform: 'translateY(-50%) rotate(45deg)',
                                                zIndex: 0,
                                            }
                                        },
                                    }}
                                    transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                                    anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                                >
                                    <MenuItem onClick={navigateToOrderHistory} style={{ display: "flex", justifyContent: "center" }}>
                                        Order History
                                    </MenuItem>
                                    <Divider sx={{ borderColor: 'rgba(0, 0, 0, 0.30)' }} />
                                    <MenuItem onClick={handleSignOut}>
                                        <Image src={signoutIcon} alt="Sign Out" width={16} height={16} />Sign Out
                                    </MenuItem>
                                </Menu>
                                :
                                <Menu
                                    anchorEl={loginEl}
                                    id="login-menu"
                                    className={styles.login_menu_list}
                                    open={openLoginLayer}
                                    onClose={handleLoginClose}
                                    PaperProps={{
                                        elevation: 0,
                                        sx: {
                                            overflow: 'visible',
                                            borderRadius: '12px',
                                            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                                            mt: 1.5,
                                            '& .MuiAvatar-root': {
                                                width: 32,
                                                height: 32,
                                                ml: -0.5,
                                                mr: 1,
                                            },
                                            '&:before': {
                                                content: '""',
                                                display: 'block',
                                                position: 'absolute',
                                                top: 0,
                                                right: 20,
                                                width: 15,
                                                height: 15,
                                                bgcolor: 'background.paper',
                                                transform: 'translateY(-50%) rotate(45deg)',
                                                zIndex: 0,
                                            }
                                        },
                                    }}
                                    transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                                    anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                                >
                                    <MenuItem selected={false} autoFocus={false} sx={{ '&:hover': { backgroundColor: 'white' } }}>
                                        {openLoginLayer ? <LoginLayer layerMode={true} brand={brandName} loginCallback={loginCallback} handleDialogClose={handleLoginClose} /> : "loading..."}
                                    </MenuItem>
                                </Menu>
                            }
                            </div>
                        </div>)
                    }
                </div>
            </div>
            <Drawer
                anchor="left"
                open={openDrawer}
                onClose={toggleDrawer(false)}
                sx={{
                    zIndex: '100000'
                }}
            >
                <div>
                    <List>
                            <HeaderMenu menuJson={menuJson || {}} deviceType="mobile" setOpenDrawer={setOpenDrawer} />
                            {
                                isUserAuthenticated &&
                                <>
                                    <ListItem onClick={navigateToOrderHistory} sx={{ padding: '13px 26px' }}>
                                        ORDER HISTORY
                                    </ListItem>
                                    <Divider sx={{ borderColor: 'rgba(0, 0, 0, 0.30)' }} />
                                    <ListItem onClick={handleSignOut} sx={{ padding: '13px 26px' }}>
                                        <Image src={signoutIcon} alt="Sign Out" width={16} height={16} style={{ paddingRight: '10px' }} /> Sign Out
                                    </ListItem>
                                </>
                            }
                            {!isUserAuthenticated && hasCtaButtons && ctaButtons.filter(c => c.id !== 'signin').map((cta) => (
                                <ListItem
                                    key={cta.id}
                                    sx={{ padding: '13px 26px', cursor: 'pointer' }}
                                    onClick={() => {
                                        if (cta.action) (window as any).openSignUpPopup?.(cta.action);
                                        else if (cta.link) window.location.href = cta.link;
                                        setOpenDrawer(false);
                                        if (cta.trackInfo) gtmClickTracking(cta.trackInfo);
                                    }}
                                >
                                    {cta.text}
                                </ListItem>
                            ))}
                        </List>
                    </div>
                </Drawer>

            {(brandName === 'gtr' || brandName === 'dynamic-rugs') && <div className={styles.header_space}></div>}
        </>
    )
}

export default HeaderComponent;
